import { useEffect, useState } from 'react'
import { Wifi, WifiOff, Sun, Moon, Pause } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useTheme } from '../contexts/ThemeContext'
import { useScope } from '../contexts/ScopeContext'
import { statusOf } from '../equipment'
import MineScopeSelect from './MineScopeSelect'
import LangSelect from './LangSelect'

// Views com entrada em nav:titles.<viewId> (crumb/title/sub).
const TITLE_VIEWS = new Set([
  'inicio', 'equipamentos', 'controlador', 'eventos', 'tracking', 'ciclos', 'furos',
  'dashboard', 'produtividade', 'timeline', 'despacho', 'geofences', 'mapas', 'alarmes',
  'admin-frota', 'admin-minas', 'admin-pits', 'admin-equipe', 'admin-zonas', 'admin-producao', 'admin-debug',
])

export default function Topbar({ view }) {
  const { t } = useTranslation()
  const { connected, devices, feedPaused, setFeedPaused } = useWS()
  const { dark, toggle } = useTheme()
  const { matchesScope } = useScope()
  // Ticker de 1s: "em operação" depende do tempo desde a última mensagem, então o
  // número muda sem frame novo (o mesmo ticker do Equipamentos e do Tracking).
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 1000)
    return () => clearInterval(id)
  }, [])
  const titles = TITLE_VIEWS.has(view)
    ? {
        crumb: t(`nav:titles.${view}.crumb`, { defaultValue: '' }),
        title: t(`nav:titles.${view}.title`, { defaultValue: '' }),
        sub:   t(`nav:titles.${view}.sub`,   { defaultValue: '' }),
      }
    : { crumb: t('nav:menu.groups.operation'), title: '', sub: '' }
  // Mesma regra do Tracking e do Início (statusOf): "em operação" é operador logado,
  // não o flag de comunicação de 30s, que dava 12 aqui e 0 nas outras telas.
  const operating = devices.filter(d => matchesScope(d) && statusOf(d).key === 'op').length

  return (
    <header className="h-16 shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex items-center gap-4 px-6">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {titles.crumb && (
          <>
            <span className="text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 shrink-0">{titles.crumb}</span>
            <span className="text-slate-300 dark:text-slate-600 shrink-0">/</span>
          </>
        )}
        <div className="min-w-0">
          <h1 className="text-base font-semibold text-slate-800 dark:text-slate-100 leading-tight truncate">{titles.title}</h1>
          <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{titles.sub}</p>
        </div>
      </div>

      <div className="hidden sm:block shrink-0">
        <MineScopeSelect />
      </div>

      <div className="flex items-center gap-4 shrink-0">
        {/* A pausa do stream vale para a sessão inteira do navegador (o servidor para de
            mandar eventos a este cliente), então o aviso vive no topo, não só no Eventos. */}
        {feedPaused && (
          <button
            onClick={() => setFeedPaused(false)}
            title={t('ops:feed.paused_hint')}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-status-stop/15 text-status-stop text-xs font-medium hover:bg-status-stop/25"
          >
            <Pause size={12} /> {t('ops:feed.paused_badge')}
          </button>
        )}
        <span className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-status-op/10 text-status-op text-xs font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-status-op" /> {t('ops:devices.in_operation', { count: operating })}
        </span>
        <div className="flex items-center gap-1.5 text-xs">
          {connected ? <Wifi size={14} className="text-status-op" /> : <WifiOff size={14} className="text-slate-400 dark:text-slate-500" />}
          <span className={connected ? 'text-status-op' : 'text-slate-400 dark:text-slate-500'}>{connected ? t('common:states.connected') : t('common:states.reconnecting')}</span>
        </div>
        <button
          onClick={toggle}
          title={dark ? t('nav:topbar.theme_light') : t('nav:topbar.theme_dark')}
          className="flex items-center text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          {dark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
        <LangSelect />
      </div>
    </header>
  )
}
